import { UNDO_LIMIT } from '../utils/constants.js';

/**
 * Undo/redo using a linear timeline of commands.
 *
 * All commands live in a single array (`_timeline`). A cursor (`_index`)
 * points to the last executed command. Undo decrements the cursor, redo
 * increments it. Multi-step undo jumps the cursor to any point.
 *
 * Each command has { execute(), undo(), description }.
 * Commands may also carry a `group` string to batch related operations
 * (e.g. a multi-tile brush stroke) for display purposes.
 */
export class CommandHistory {
  constructor() {
    /** All commands in order. */
    this._timeline = [];
    /** Index of the last executed command (-1 = nothing executed). */
    this._index = -1;
    this._listeners = new Set();
  }

  /** Execute a new command and append to timeline. Discards any redo future. */
  execute(command) {
    command.execute();
    this._pushToTimeline(command);
  }

  /** Record a pre-executed command (already applied to state). */
  push(command) {
    this._pushToTimeline(command);
  }

  /** Undo the most recent command. */
  undo() {
    if (this._index < 0) return false;
    this._timeline[this._index].undo();
    this._index--;
    this._notify();
    return true;
  }

  /** Redo the next command. */
  redo() {
    if (this._index >= this._timeline.length - 1) return false;
    this._index++;
    this._timeline[this._index].execute();
    this._notify();
    return true;
  }

  /**
   * Jump to a specific point in the timeline.
   * Undoes or redoes as many commands as needed to reach the target index.
   * @param {number} targetIndex — -1 to undo everything, or 0..length-1
   */
  goTo(targetIndex) {
    if (this._navigating) return; // Re-entrancy guard
    targetIndex = Math.max(-1, Math.min(targetIndex, this._timeline.length - 1));
    if (targetIndex === this._index) return;

    this._navigating = true;
    try {
      if (targetIndex < this._index) {
        while (this._index > targetIndex) {
          try { this._timeline[this._index].undo(); } catch (e) { console.error('Undo failed:', e); }
          this._index--;
        }
      } else {
        while (this._index < targetIndex) {
          this._index++;
          try { this._timeline[this._index].execute(); } catch (e) {
            console.error('Redo failed:', e);
            this._index--; // Revert index — command was not applied
            break;
          }
        }
      }
    } finally {
      this._navigating = false;
    }
    this._notify();
  }

  /** Undo/redo multiple steps at once. Negative = undo, positive = redo. */
  step(n) {
    this.goTo(this._index + n);
  }

  canUndo() { return this._index >= 0; }
  canRedo() { return this._index < this._timeline.length - 1; }

  /** Current position in the timeline (-1 = before all commands). */
  get currentIndex() { return this._index; }

  /** Total number of commands in the timeline. */
  get length() { return this._timeline.length; }

  /**
   * Get the timeline for display purposes.
   * Returns array of { description, index, isCurrent, isFuture }.
   */
  getTimeline() {
    return this._timeline.map((cmd, i) => ({
      description: cmd.description || `Action ${i + 1}`,
      group: cmd.group || null,
      index: i,
      isCurrent: i === this._index,
      isFuture: i > this._index,
    }));
  }

  clear() {
    this._timeline.length = 0;
    this._index = -1;
    this._notify();
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  // ─── Internal ──────────────────────────────────────────────────

  _pushToTimeline(command) {
    // Discard any redo future (commands after current index)
    if (this._index < this._timeline.length - 1) {
      this._timeline.length = this._index + 1;
    }
    this._timeline.push(command);
    this._index = this._timeline.length - 1;

    // Enforce limit — drop oldest commands
    if (this._timeline.length > UNDO_LIMIT) {
      const excess = this._timeline.length - UNDO_LIMIT;
      this._timeline.splice(0, excess);
      this._index -= excess;
    }
    this._notify();
  }

  _notify() {
    for (const fn of this._listeners) fn();
  }
}
